import React from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, AuthUser } from '../contexts/AuthContext';
import { isAtLeast, enforcesPasswordChange } from '../utils/roles';

interface RoleRouteProps {
  children: React.ReactNode;
  roles: AuthUser['role'][];
}

export function RoleRoute({ children, roles }: RoleRouteProps) {
  const { user, isAuthenticated, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!isAuthenticated) {
    // Preserve the intended destination so the login page can return the user
    // here after authenticating (important for in-memory token: a hard URL load
    // starts unauthenticated, so login must redirect back).
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Force first-login password change for staff/operator/customer accounts.
  // Legacy KDU guests (Attendee/Delegate) are exempt. Runs before the role
  // check so a temp-password user is sent to set their password first.
  if (
    mustChangePassword &&
    enforcesPasswordChange(user?.role) &&
    location.pathname !== '/change-password'
  ) {
    return <Navigate to="/change-password" replace />;
  }

  // Use hierarchy: if user's role is at or above any of the required roles, allow access
  const hasAccess = user && roles.some(requiredRole => isAtLeast(user.role, requiredRole));

  if (!hasAccess) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You do not have permission to access this resource.</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1.2rem',
            fontSize: '0.85rem',
            fontWeight: 600,
            backgroundColor: '#1a6b3c',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          ← Go Back
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
